import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { NotesPanel, NotesIcon, formatWallClock } from './MeetingNotesPanel';
import { renderFormattedText } from '../utils/simpleRichText';
import { USER_ROLES } from '../utils/rolePermissions';
import Icon from './Icon';
import Avatar from './Avatar';
import meetingLogo from '../icons/v-meeting-logo.png';
import '../styles/MeetingLobby.css';
import '../styles/LiveKitMeetingRoom.css';

const MicIcon = () => (
  <svg className="meeting-lobby-mic-svg" viewBox="0 0 15 21" fill="none">
    <path d="M14.1665 8.66866V10.2307C14.1665 12.0493 13.5652 13.626 12.3625 14.9607C11.1598 16.2953 9.66384 17.058 7.87451 17.2487V18.8767H11.0205C11.2405 18.8767 11.4238 18.9573 11.5705 19.1187C11.7172 19.28 11.7978 19.4633 11.8125 19.6687C11.8272 19.874 11.7465 20.0573 11.5705 20.2187C11.3945 20.38 11.2112 20.4607 11.0205 20.4607H3.16651C2.94651 20.4607 2.76317 20.38 2.61651 20.2187C2.46984 20.0573 2.38917 19.874 2.37451 19.6687C2.35984 19.4633 2.44051 19.28 2.61651 19.1187C2.79251 18.9573 2.97584 18.8767 3.16651 18.8767H6.31251V17.2487C4.53784 17.058 3.04184 16.2953 1.82451 14.9607C0.607175 13.626 0.00584114 12.0493 0.0205078 10.2307V8.66866C0.0205078 8.44866 0.101174 8.26532 0.262508 8.11866C0.423841 7.97199 0.607175 7.89132 0.812508 7.87666C1.01784 7.86199 1.20117 7.94266 1.36251 8.11866C1.52384 8.29465 1.59717 8.47799 1.58251 8.66866V10.2307C1.58251 11.7413 2.12517 13.0393 3.21051 14.1247C4.29584 15.21 5.58651 15.7453 7.08251 15.7307C8.57851 15.716 9.87651 15.1807 10.9765 14.1247C12.0765 13.0687 12.6118 11.7707 12.5825 10.2307V8.66866C12.5825 8.44866 12.6632 8.26532 12.8245 8.11866C12.9858 7.97199 13.1692 7.89132 13.3745 7.87666C13.5798 7.86199 13.7632 7.94266 13.9245 8.11866C14.0858 8.29465 14.1665 8.47799 14.1665 8.66866ZM11.0205 3.96066V10.2307C11.0205 11.316 10.6392 12.24 9.87651 13.0027C9.11384 13.7653 8.18251 14.154 7.08251 14.1687C5.98251 14.1833 5.05851 13.7947 4.31051 13.0027C3.56251 12.2107 3.18117 11.2867 3.16651 10.2307V3.96066C3.16651 2.87532 3.54784 1.94399 4.31051 1.16666C5.07317 0.389322 5.99717 0.00798893 7.08251 0.0226555C8.16784 0.037322 9.09917 0.418655 9.87651 1.16666C10.6538 1.91466 11.0352 2.84599 11.0205 3.96066Z" fill="currentColor" />
  </svg>
);

const CameraIcon = () => (
  <svg className="meeting-lobby-camera-svg" viewBox="0 0 26 26" fill="none">
    <path d="M5.41667 6.5H15.1667C16.3633 6.5 17.3333 7.47005 17.3333 8.66667V17.3333C17.3333 18.53 16.3633 19.5 15.1667 19.5H5.41667C4.22005 19.5 3.25 18.53 3.25 17.3333V8.66667C3.25 7.47005 4.22005 6.5 5.41667 6.5Z" strokeWidth="1.95" stroke="currentColor" />
    <path d="M17.332 10.834L22.7487 7.58398V18.4173L17.332 15.1673" strokeWidth="1.95" strokeLinejoin="round" stroke="currentColor" />
  </svg>
);

const InfoIcon = () => <Icon name="info-circled" size="14px" />;

// Recording Consent's checked state is a green "check" glyph from the icon
// font (not the browser's native checkbox render), so the native input is
// kept only for its a11y/change semantics and visually hidden.
const ConsentCheckbox = ({ checked, onChange, disabled, readOnly }) => (
  <span className={`meeting-lobby-checkbox-box ${checked ? 'checked' : ''}`}>
    <input
      type="checkbox"
      className="meeting-lobby-checkbox-native"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      readOnly={readOnly}
    />
    <Icon name="check" className="meeting-lobby-checkbox-icon" />
  </span>
);

const SendIcon = () => <Icon name="paper-plane-empty" size="16px" />;

const ParticipantsIcon = () => <Icon name="user-add" size="18px" />;

const SearchIcon = () => <Icon name="search" size="14px" />;

const ChatBubbleIcon = () => <Icon name="comment-empty" size="18px" />;

const ChevronLeftIcon = () => <Icon name="chevron-left" size="16px" />;

// Icon.js's "chevron-up" entry is mapped to the same codepoint as
// "chevron-down" (a pre-existing bug there), so this goes straight to the
// font's actual up-open glyph instead.
const ChevronUpSmallIcon = () => <i className="vl-icon" style={{ fontSize: '10px' }}>{String.fromCharCode(0xe819)}</i>;

const ChevronRightIcon = () => <Icon name="chevron-right" size="16px" />;

const AGENCY_SIDE_ROLES = ['organizer', USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.OPERATOR];

const isAgencySideParticipant = (p) => !p.is_external && AGENCY_SIDE_ROLES.includes(p.role);

const LobbyPersonRow = ({ person, agencyId, agencyName, brandName }) => (
  <div className="meeting-lobby-person-row">
    <Avatar user={{ _id: person.user_id, name: person.name, email: person.email, agency_id: agencyId }} size="sm" />
    <div className="meeting-lobby-person-info">
      <span className="meeting-lobby-person-name">{person.name || person.email}</span>
      <span className="meeting-lobby-person-sub">
        {isAgencySideParticipant(person) ? agencyName : (brandName || 'Guest')}
      </span>
    </div>
  </div>
);

const LobbyParticipantsPanel = ({ participants, onToggle, agencyId, agencyName, brandName }) => {
  const [search, setSearch] = useState('');

  const filtered = participants.filter((p) =>
    (p.name || p.email || '').toLowerCase().includes(search.trim().toLowerCase())
  );
  const contributors = filtered.filter(isAgencySideParticipant);
  const alsoInvited = filtered.filter((p) => !isAgencySideParticipant(p));

  return (
    <div className="velvet-side-panel velvet-notes-panel">
      <div className="velvet-panel-header">
        <span>
          <ParticipantsIcon /> People
        </span>
        <button className="velvet-panel-collapse-btn" onClick={onToggle} title="Collapse">
          <ChevronLeftIcon />
        </button>
      </div>

      <div className="meeting-lobby-people-search">
        <SearchIcon />
        <input
          type="text"
          placeholder="Search people"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="velvet-panel-list">
        <div className="meeting-lobby-people-subtitle">In the Meeting</div>

        <div className="meeting-lobby-people-section-label">Contributors</div>
        {contributors.length === 0 && <div className="meeting-lobby-people-empty">No contributors found.</div>}
        {contributors.map((p, i) => (
          <LobbyPersonRow key={p.user_id || p.email || i} person={p} agencyId={agencyId} agencyName={agencyName} brandName={brandName} />
        ))}

        <div className="meeting-lobby-people-section-label">Also Invited</div>
        {alsoInvited.length === 0 && <div className="meeting-lobby-people-empty">Nobody else invited.</div>}
        {alsoInvited.map((p, i) => (
          <LobbyPersonRow key={p.user_id || p.email || i} person={p} agencyId={agencyId} agencyName={agencyName} brandName={brandName} />
        ))}
      </div>
    </div>
  );
};

// Sending stays disabled pre-join - there's no LiveKit room yet to deliver through.
const LobbyChatPreview = ({ meetingId, agencyId, onToggle }) => {
  const [history, setHistory] = useState([]);
  const listRef = useRef(null);

  useEffect(() => {
    axios
      .get(`/api/livekit/meetings/${meetingId}/chat`, {
        params: agencyId ? { agency_id: agencyId } : {},
        withCredentials: true
      })
      .then((res) => {
        if (res.data.success) setHistory(res.data.messages);
      })
      .catch((err) => console.error('Failed to load chat history:', err));
  }, [meetingId, agencyId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [history]);

  return (
    <div className="velvet-side-panel velvet-chat-panel">
      <div className="velvet-panel-header">
        <span>
          <ChatBubbleIcon /> Chat
        </span>
        <button className="velvet-panel-collapse-btn" onClick={onToggle} title="Collapse">
          <ChevronRightIcon />
        </button>
      </div>

      <div className="velvet-panel-list" ref={listRef}>
        {history.length === 0 && (
          <div className="meeting-lobby-chat-empty">No messages yet.</div>
        )}
        {history.map((m) => (
          <div key={m.id} className="velvet-chat-entry">
            <div className="velvet-chat-entry-meta">
              <span className="velvet-chat-entry-name">{m.sender_name}</span>
              <span className="velvet-chat-entry-time">{formatWallClock(m.created_at)}</span>
            </div>
            <div className="velvet-chat-entry-text" dangerouslySetInnerHTML={{ __html: renderFormattedText(m.text) }} />
          </div>
        ))}
      </div>

      <div className="velvet-panel-compose">
        <textarea disabled placeholder="You can send messages once the meeting starts" />
        <div className="velvet-panel-compose-toolbar">
          <button type="button" className="velvet-panel-send" disabled title="Available once the meeting starts">
            <SendIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

const LobbyBottomTray = ({
  notesOpen,
  onToggleNotes,
  participantsOpen,
  onToggleParticipants,
  chatOpen,
  onToggleChat
}) => (
  <div className="meeting-lobby-bottom-bar">
    <div className="meeting-lobby-bottom-bar-group">
      <button
        type="button"
        className={`velvet-tray-btn ${notesOpen ? 'active' : ''}`}
        onClick={onToggleNotes}
        title="Notes"
      >
        <NotesIcon />
      </button>
      <button
        type="button"
        className={`velvet-tray-btn ${participantsOpen ? 'active' : ''}`}
        onClick={onToggleParticipants}
        title="People"
      >
        <ParticipantsIcon />
      </button>
    </div>
    <div className="meeting-lobby-bottom-bar-group">
      <button
        type="button"
        className={`velvet-tray-btn ${chatOpen ? 'active' : ''}`}
        onClick={onToggleChat}
        title="Chat"
      >
        <ChatBubbleIcon />
      </button>
    </div>
  </div>
);

const REQUEST_POLL_INTERVAL_MS = 2500;

// Pre-join screen. Three variants:
//  - 'host': agency staff. Name/agency come from their Velvet account and
//    aren't editable, and recording consent is pre-checked.
//  - 'client': a logged-in client. Name/company are auto-filled from their
//    account and the meeting's brand, consent defaults to checked but can
//    be unchecked, and they join directly.
//  - 'guest' (default, no account): the meeting's participants were chosen
//    at scheduling time, so we already know who's an internal member vs.
//    an external guest. A visitor with no session first types their email
//    (checked against that participant list) - if it belongs to an invited
//    member they're told to sign in (so they can't quietly sit in as an
//    unidentified guest just because they're logged out); anyone else
//    (true guests, including from a different email than they were
//    invited at) proceeds through the normal camera/mic preview, blank
//    name/team fields, required recording consent, and a request-to-join
//    -> host-approval wait before the real LiveKitRoom connection is made.
// Anyone can join at any time - there's no separate "start the meeting"
// step; the room simply goes active the moment the first person connects.
const MeetingLobby = ({ meetingId, agencyId, onJoined, variant = 'guest', user, onJoin, joining, joinError }) => {
  const isHost = variant === 'host';
  const isClientVariant = variant === 'client';
  const isAnonymous = !isHost && !isClientVariant;

  const [meeting, setMeeting] = useState(null);
  const [name, setName] = useState('');
  const [team, setTeam] = useState('');
  const [consent, setConsent] = useState(isHost || isClientVariant);
  const [micEnabled, setMicEnabled] = useState(true);
  const [camEnabled, setCamEnabled] = useState(true);
  const [audioLevel, setAudioLevel] = useState(0);
  const [micGain, setMicGain] = useState(() => {
    const saved = localStorage.getItem('livekit_mic_gain');
    return saved !== null ? parseFloat(saved) : 1.0;
  });
  const [showVoiceMenu, setShowVoiceMenu] = useState(false);
  const [voiceMenuPos, setVoiceMenuPos] = useState(null);
  const [phase, setPhase] = useState(isAnonymous ? 'identify' : 'form'); // identify | requires_login | form | waiting | denied | error
  const [errorMsg, setErrorMsg] = useState('');

  const [identifyEmail, setIdentifyEmail] = useState('');
  const [identifyError, setIdentifyError] = useState('');
  const [checkingInvite, setCheckingInvite] = useState(false);
  const [requiresLoginName, setRequiresLoginName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [mediaError, setMediaError] = useState('');

  const [notesOpen, setNotesOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const pollRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micGroupRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchMeeting = async () => {
      try {
        const res = await axios.get(`/api/livekit/meetings/${meetingId}`, {
          params: agencyId ? { agency_id: agencyId } : {},
          withCredentials: true
        });
        if (cancelled || !res.data.success) return;

        setMeeting(res.data.meeting);
      } catch (err) {
        console.error('Failed to load meeting details:', err);
      }
    };

    fetchMeeting();

    return () => {
      cancelled = true;
    };
  }, [meetingId, agencyId]);

  useEffect(() => {
    let cancelled = false;

    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;

        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            const audioCtx = new AudioCtx();
            const source = audioCtx.createMediaStreamSource(new MediaStream([audioTrack]));
            const analyser = audioCtx.createAnalyser();
            analyser.fftSize = 256;
            source.connect(analyser);
            audioContextRef.current = audioCtx;
            analyserRef.current = analyser;
          } catch (err) {
            console.error('Could not set up mic level meter:', err);
          }
        }
      })
      .catch((err) => {
        console.error('Could not access camera/microphone for lobby preview:', err);
        if (cancelled) return;
        const message =
          err.name === 'NotAllowedError'
            ? 'Camera/microphone access was blocked. Check your browser permissions and reload.'
            : err.name === 'NotFoundError'
              ? 'No camera or microphone was found on this device.'
              : 'Could not access your camera/microphone.';
        setMediaError(message);
      });

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      analyserRef.current = null;
      audioContextRef.current?.close();
      audioContextRef.current = null;
    };
  }, []);

  // Live mic level for the lobby preview, mirroring the vertical meter shown
  // next to each participant's name in the actual meeting room.
  useEffect(() => {
    const dataArray = new Uint8Array(128);

    const interval = setInterval(() => {
      const analyser = analyserRef.current;
      if (!analyser) return;
      analyser.getByteFrequencyData(dataArray);
      const avg = dataArray.reduce((sum, v) => sum + v, 0) / dataArray.length;
      const level = Math.pow(avg / 255, 0.5) * 100 * micGain;
      setAudioLevel(Math.min(level, 100));
    }, 100);

    return () => clearInterval(interval);
  }, [micGain]);

  // Close the voice controls menu on outside click
  useEffect(() => {
    if (!showVoiceMenu) return;

    const handleClickOutside = (e) => {
      if (micGroupRef.current && !micGroupRef.current.contains(e.target)) {
        setShowVoiceMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showVoiceMenu]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const stopPreview = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const toggleMic = () => {
    const track = streamRef.current?.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setMicEnabled(track.enabled);
    localStorage.setItem('velvet_mic_enabled', track.enabled.toString());
  };

  // Camera off actually releases the device (stop(), not just .enabled),
  // matching Google Meet - the camera's hardware light stays on either
  // way if you only disable the track, since the device is still held
  // open. Mic intentionally stays untouched when muted (see toggleMic) -
  // that's also LiveKit's own default, to avoid Bluetooth headsets
  // switching profiles every time you mute.
  const toggleCam = async () => {
    if (camEnabled) {
      const track = streamRef.current?.getVideoTracks()[0];
      if (track) {
        track.stop();
        streamRef.current.removeTrack(track);
      }
      setCamEnabled(false);
      localStorage.setItem('velvet_camera_enabled', 'false');
      return;
    }

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({ video: true });
      const newTrack = newStream.getVideoTracks()[0];
      streamRef.current?.addTrack(newTrack);
      if (videoRef.current) videoRef.current.srcObject = streamRef.current;
      setCamEnabled(true);
      localStorage.setItem('velvet_camera_enabled', 'true');
    } catch (err) {
      console.error('Could not re-enable camera:', err);
      setMediaError('Could not access your camera.');
    }
  };

  const handleGainChange = (e) => {
    const newGain = parseFloat(e.target.value);
    setMicGain(newGain);
    localStorage.setItem('livekit_mic_gain', newGain.toString());
  };

  const handleIdentifySubmit = async () => {
    const trimmedEmail = identifyEmail.trim();
    if (!trimmedEmail) {
      setIdentifyError('Please enter your email.');
      return;
    }

    setIdentifyError('');
    setCheckingInvite(true);

    try {
      const res = await axios.post(
        `/api/livekit/meetings/${meetingId}/check-invite`,
        { email: trimmedEmail, agency_id: agencyId },
        { withCredentials: true }
      );

      if (res.data.requires_login) {
        setRequiresLoginName(res.data.name || '');
        setPhase('requires_login');
      } else {
        setGuestEmail(trimmedEmail);
        setPhase('form');
      }
    } catch (err) {
      setIdentifyError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setCheckingInvite(false);
    }
  };

  // Sends them to the app's one actual login screen - not a login UI of our
  // own - carrying the join link along so the normal Google/Microsoft
  // buttons there bring them back here afterward instead of to a dashboard.
  const handleSignIn = () => {
    const returnPath = `/join-meeting/${meetingId}${agencyId ? `?agency_id=${agencyId}` : ''}`;
    window.location.href = `/?next=${encodeURIComponent(returnPath)}`;
  };

  const completeJoin = async (guestName) => {
    try {
      stopPreview();
      const res = await axios.post(
        `/api/livekit/meetings/${meetingId}/join`,
        { is_guest: true, name: guestName, email: guestEmail, agency_id: agencyId, recording_consent: consent },
        { withCredentials: true }
      );

      if (res.data.success) {
        onJoined({
          token: res.data.token,
          wsUrl: res.data.ws_url,
          participantIdentity: res.data.participant_identity,
          participantName: res.data.participant_name,
          meeting
        });
      } else {
        setErrorMsg(res.data.error || 'Failed to join meeting.');
        setPhase('form');
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Failed to join meeting.');
      setPhase('form');
    }
  };

  // Shared by the anonymous-guest flow below and the logged-in host/client
  // "not on the invite list" flow - polls one join request until the host
  // resolves it, then hands off to whichever of these the caller needs.
  const pollJoinRequest = (requestId, onApproved, onDenied) => {
    pollRef.current = setInterval(async () => {
      try {
        const statusRes = await axios.get(
          `/api/livekit/meetings/${meetingId}/join-requests/${requestId}`,
          { params: agencyId ? { agency_id: agencyId } : {}, withCredentials: true }
        );

        const status = statusRes.data.request?.status;

        if (status === 'approved') {
          clearInterval(pollRef.current);
          onApproved();
        } else if (status === 'denied') {
          clearInterval(pollRef.current);
          onDenied();
        }
      } catch (err) {
        console.error('Error polling join request status:', err);
      }
    }, REQUEST_POLL_INTERVAL_MS);
  };

  const handleJoinClick = async () => {
    // The lobby's camera/mic state only reaches localStorage via toggleMic/
    // toggleCam above, so an untouched default (left at its initial true)
    // never gets written - leaving the meeting room to pick up a stale value
    // from a previous session instead of what's actually showing here. Write
    // the current state now, at the moment the user commits to joining.
    localStorage.setItem('velvet_mic_enabled', micEnabled.toString());
    localStorage.setItem('velvet_camera_enabled', camEnabled.toString());

    if (isHost || isClientVariant) {
      // Most logged-in hosts/clients are already invited and connect
      // straight away. Someone who isn't gets a 403 back with a join
      // request already created for their account server-side - from here
      // it's the same wait/poll/retry as the guest flow below, just without
      // a name/team form since we already know who they are.
      const result = await onJoin?.(consent);
      if (result?.pendingApproval) {
        setPhase('waiting');
        pollJoinRequest(
          result.requestId,
          () => onJoin?.(consent),
          () => setPhase('denied')
        );
        return;
      }
      stopPreview();
      return;
    }

    const trimmedName = name.trim();
    if (!trimmedName) {
      setErrorMsg('Please enter your name.');
      return;
    }
    if (!consent) {
      setErrorMsg('Please confirm recording consent to join.');
      return;
    }

    setErrorMsg('');
    setPhase('waiting');

    try {
      const res = await axios.post(
        `/api/livekit/meetings/${meetingId}/join-requests`,
        { name: trimmedName, team: team.trim(), agency_id: agencyId },
        { withCredentials: true }
      );

      if (!res.data.success) {
        setErrorMsg(res.data.error || 'Failed to request to join.');
        setPhase('form');
        return;
      }

      pollJoinRequest(
        res.data.request_id,
        () => completeJoin(trimmedName),
        () => setPhase('denied')
      );
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Failed to request to join.');
      setPhase('form');
    }
  };

  // The URL's agencyId prop only exists for an unauthenticated guest (no
  // session) to tell the backend which agency owns this meeting - for
  // anyone logged in it's often absent, which was silently breaking every
  // invited participant's avatar here (see the matching resolvedAgencyId
  // fix in LiveKitMeetingRoom.js, for the in-room version of this panel).
  const resolvedAgencyId = meeting?.agency_id || agencyId;

  const formattedTime = meeting?.scheduled_start
    ? new Date(meeting.scheduled_start).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      })
    : '';

  const companyName = meeting?.brand?.name || meeting?.organization?.name || '';
  const meetingTitle = meeting?.title || 'Meeting';
  const lobbyTitle = companyName ? `${companyName} - ${meetingTitle}` : meetingTitle;

  const previewUser = user
    ? { _id: user._id, name: user.name, email: user.email, agency_id: resolvedAgencyId }
    : { name: name || guestEmail };

  return (
    <div className={`meeting-lobby-container${isHost ? ' meeting-lobby-container--agency' : ''}`}>
      <div className={`meeting-lobby-header${isHost ? ' meeting-lobby-header--agency' : ''}`}>
        <img src={meetingLogo} alt="Velvet Meetings" className="meeting-lobby-logo-img" />
      </div>

      <div className="meeting-lobby-body">
      <div className="meeting-lobby-card">
        <div className="meeting-lobby-card-header">
          <h2>{lobbyTitle}</h2>
        </div>

        <div className="meeting-lobby-card-body">
          <div className="meeting-lobby-preview">
            {mediaError ? (
              <div className="meeting-lobby-media-error">{mediaError}</div>
            ) : (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="meeting-lobby-video"
                  style={{ display: camEnabled ? 'block' : 'none' }}
                />
                {!camEnabled && (
                  <div className="meeting-lobby-camera-off">
                    <Avatar user={previewUser} size="xl" />
                  </div>
                )}
              </>
            )}
          </div>

          <div className="meeting-lobby-preview-controls">
            <div className="meeting-lobby-control-group" ref={micGroupRef}>
              <button
                type="button"
                className={`meeting-lobby-control-btn ${!micEnabled ? 'off' : ''}`}
                onClick={toggleMic}
                disabled={!!mediaError}
                title={mediaError || (micEnabled ? 'Mute microphone' : 'Unmute microphone')}
              >
                <MicIcon />
              </button>
              <div
                className={`meeting-lobby-mic-meter meeting-lobby-mic-meter--${
                  !micEnabled ? 'muted' : audioLevel >= 85 ? 'hot' : audioLevel >= 60 ? 'warning' : 'good'
                }`}
                style={{ '--vm-level': `${Math.max(6, audioLevel)}%` }}
                title="Microphone level"
              />
              <button
                type="button"
                className={`meeting-lobby-control-chevron ${showVoiceMenu ? 'active' : ''}`}
                onClick={() => {
                  if (!showVoiceMenu && micGroupRef.current) {
                    const rect = micGroupRef.current.getBoundingClientRect();
                    setVoiceMenuPos({ top: rect.top - 12, left: rect.left + rect.width / 2 });
                  }
                  setShowVoiceMenu((v) => !v);
                }}
                title="Voice controls"
              >
                <ChevronUpSmallIcon />
              </button>

              {showVoiceMenu && voiceMenuPos && (
                // Positioned fixed (viewport coordinates) instead of the
                // room's absolute/bottom-anchored version - .meeting-lobby-card
                // clips overflow for its rounded corners, which was cutting
                // off the left side of this menu when it tried to center
                // itself under the mic button via absolute positioning.
                <div
                  className="voice-controls-menu"
                  style={{
                    position: 'fixed',
                    top: voiceMenuPos.top,
                    left: voiceMenuPos.left,
                    bottom: 'auto',
                    transform: 'translate(-50%, -100%)'
                  }}
                >
                  <div className="voice-controls-menu-header">Voice Controls</div>
                  <div className="gain-control">
                    <label className="gain-label">Mic Boost: {micGain.toFixed(1)}x</label>
                    <input
                      type="range"
                      min="0.5"
                      max="3.0"
                      step="0.1"
                      value={micGain}
                      onChange={handleGainChange}
                      className="gain-slider"
                    />
                    <div className="gain-marks">
                      <span>0.5x</span>
                      <span>1.5x</span>
                      <span>3.0x</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="meeting-lobby-control-divider" />

            <div className="meeting-lobby-control-group">
              <button
                type="button"
                className={`meeting-lobby-control-btn ${!camEnabled ? 'off' : ''}`}
                onClick={toggleCam}
                disabled={!!mediaError}
                title={mediaError || (camEnabled ? 'Turn off camera' : 'Turn on camera')}
              >
                <CameraIcon />
              </button>
              <button type="button" className="meeting-lobby-control-chevron" title="Camera options">
                <ChevronUpSmallIcon />
              </button>
            </div>

            <button type="button" className="meeting-lobby-control-more" title="More options">
              &#8942;
            </button>
          </div>

          <div className="meeting-lobby-details">
            {formattedTime && <div className="meeting-lobby-time">{formattedTime}</div>}

            {meeting?.description && (
              <div className="meeting-lobby-agenda">
                <strong>Agenda:</strong> {meeting.description}
              </div>
            )}

            {phase === 'identify' ? (
              <>
                <p className="meeting-lobby-identify-prompt">
                  Enter the email you were invited with to continue.
                </p>
                <input
                  type="email"
                  className="meeting-lobby-input"
                  placeholder="Your email"
                  value={identifyEmail}
                  onChange={(e) => setIdentifyEmail(e.target.value)}
                  autoComplete="off"
                  data-1p-ignore="true"
                  data-lpignore="true"
                  data-bwignore="true"
                  data-form-type="other"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleIdentifySubmit(); }}
                />

                {identifyError && <div className="meeting-lobby-error">{identifyError}</div>}

                <button
                  type="button"
                  className="meeting-lobby-join-btn"
                  disabled={checkingInvite}
                  onClick={handleIdentifySubmit}
                >
                  {checkingInvite ? 'Checking…' : 'Continue'}
                </button>
              </>
            ) : phase === 'requires_login' ? (
              <>
                <p className="meeting-lobby-identify-prompt">
                  {requiresLoginName
                    ? `This meeting was sent to ${requiresLoginName}'s Velvet account. Please sign in to join.`
                    : 'This email belongs to a Velvet account. Please sign in to join.'}
                </p>

                <button
                  type="button"
                  className="meeting-lobby-join-btn"
                  onClick={handleSignIn}
                >
                  Sign In
                </button>

                <button
                  type="button"
                  className="meeting-lobby-link-btn"
                  onClick={() => { setPhase('identify'); setIdentifyError(''); }}
                >
                  Wrong email? Go back
                </button>
              </>
            ) : (
              <>
                {phase === 'form' && isHost && (
                  <>
                    <div className="meeting-lobby-identity-box">{user?.name || user?.email || 'Host'}</div>
                    <div className="meeting-lobby-identity-box">{user?.agency_name || 'Velvet'}</div>

                    <label className="meeting-lobby-consent meeting-lobby-consent-locked">
                      <ConsentCheckbox checked readOnly disabled />
                      Recording Consent
                      <span
                        className="meeting-lobby-info"
                        title="This meeting may be recorded for quality and training purposes."
                      >
                        <InfoIcon />
                      </span>
                    </label>

                    {joinError && <div className="meeting-lobby-error">{joinError}</div>}

                    <button
                      type="button"
                      className="meeting-lobby-join-btn meeting-lobby-start-btn"
                      disabled={!!joining}
                      onClick={handleJoinClick}
                    >
                      {joining ? 'Joining…' : 'Join Meeting'}
                    </button>
                  </>
                )}

                {phase === 'form' && isClientVariant && (
                  <>
                    <div className="meeting-lobby-identity-box">{user?.name || user?.email || 'Guest'}</div>
                    {companyName && <div className="meeting-lobby-identity-box">{companyName}</div>}

                    <label className="meeting-lobby-consent">
                      <ConsentCheckbox checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                      Recording Consent
                      <span
                        className="meeting-lobby-info"
                        title="This meeting may be recorded for quality and training purposes."
                      >
                        <InfoIcon />
                      </span>
                    </label>

                    {joinError && <div className="meeting-lobby-error">{joinError}</div>}

                    <button
                      type="button"
                      className="meeting-lobby-join-btn"
                      disabled={!!joining}
                      onClick={handleJoinClick}
                    >
                      {joining ? 'Joining…' : 'Join Meeting'}
                    </button>
                  </>
                )}

                {phase === 'form' && !isHost && !isClientVariant && (
                  <>
                    <input
                      type="text"
                      className="meeting-lobby-input"
                      placeholder="Your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      autoComplete="off"
                      data-1p-ignore="true"
                      data-lpignore="true"
                      data-bwignore="true"
                      data-form-type="other"
                    />
                    <input
                      type="text"
                      className="meeting-lobby-input"
                      placeholder="Your team / organization"
                      value={team}
                      onChange={(e) => setTeam(e.target.value)}
                      autoComplete="off"
                      data-1p-ignore="true"
                      data-lpignore="true"
                      data-bwignore="true"
                      data-form-type="other"
                    />
                    <label className="meeting-lobby-consent">
                      <ConsentCheckbox checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                      Recording Consent
                      <span
                        className="meeting-lobby-info"
                        title="This meeting may be recorded for quality and training purposes."
                      >
                        <InfoIcon />
                      </span>
                    </label>

                    {errorMsg && <div className="meeting-lobby-error">{errorMsg}</div>}

                    <button
                      type="button"
                      className="meeting-lobby-join-btn"
                      disabled={!consent}
                      onClick={handleJoinClick}
                    >
                      Join Meeting
                    </button>
                  </>
                )}

                {phase === 'waiting' && (
                  <div className="meeting-lobby-waiting">
                    <div className="meeting-lobby-spinner" />
                    <p>Waiting for the host to let you in&hellip;</p>
                  </div>
                )}

                {phase === 'denied' && (
                  <div className="meeting-lobby-denied">
                    <p>The host declined your request to join.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      </div>

      {isHost && (notesOpen || participantsOpen) && (
        <div className="meeting-lobby-panel-column meeting-lobby-panel-column--left">
          {notesOpen ? (
            <NotesPanel meetingId={meetingId} onToggle={() => setNotesOpen(false)} collapseIcon={ChevronLeftIcon} />
          ) : (
            <LobbyParticipantsPanel
              participants={meeting?.participants || []}
              onToggle={() => setParticipantsOpen(false)}
              agencyId={resolvedAgencyId}
              agencyName={user?.agency_name || 'Velvet'}
              brandName={companyName}
            />
          )}
        </div>
      )}

      {isHost && chatOpen && (
        <div className="meeting-lobby-panel-column meeting-lobby-panel-column--right">
          <LobbyChatPreview meetingId={meetingId} agencyId={agencyId} onToggle={() => setChatOpen(false)} />
        </div>
      )}

      {isHost && (
        <LobbyBottomTray
          notesOpen={notesOpen}
          onToggleNotes={() => {
            setNotesOpen((o) => !o);
            setParticipantsOpen(false);
          }}
          participantsOpen={participantsOpen}
          onToggleParticipants={() => {
            setParticipantsOpen((o) => !o);
            setNotesOpen(false);
          }}
          chatOpen={chatOpen}
          onToggleChat={() => setChatOpen((o) => !o)}
        />
      )}
    </div>
  );
};

export default MeetingLobby;
